import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Check,
  ChevronRight,
  Copy,
  FileDown,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import { OtcMainTab, OtcRequestItem } from "../types";
import { CustomerAvatar } from "../components/CustomerAvatar";
import { MetricCard } from "../components/MetricCard";
import { OtcFilterDropdown } from "../components/OtcFilterDropdown";
import {
  HeadingText,
  Select,
  Tabs,
  PaginationControls,
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "../../../shared/ui";

const INITIAL_OTC_REQUESTS: OtcRequestItem[] = [];

const currencySelectorOptions = [
  { label: "NG Naira", value: "NGN", icon: "🇳🇬" },
  { label: "US Dollar", value: "USD", icon: "🇺🇸" },
  { label: "GB Pound", value: "GBP", icon: "🇬🇧" },
  { label: "Euro", value: "EUR", icon: "🇪🇺" },
];

const timeFilterOptions = [
  { label: "All Time", value: "all" },
  { label: "Today", value: "today" },
  { label: "Past 7 days", value: "7days" },
  { label: "Past 30 days", value: "30days" },
  { label: "Past 1 year", value: "1year" },
];

const currencyFilterOptions = [
  { label: "All", value: "All" },
  { label: "NG Naira (NGN)", value: "NGN" },
  { label: "US Dollar (USD)", value: "USD" },
  { label: "GB Pound (GBP)", value: "GBP" },
  { label: "Euro (EUR)", value: "EUR" },
];

const statusFilterOptions = [
  { label: "All", value: "All" },
  { label: "Under review", value: "Under review" },
  { label: "Request approved", value: "Request approved" },
  { label: "Offer sent", value: "Offer sent" },
  { label: "Offer accepted", value: "Offer accepted" },
  { label: "Needs approval", value: "Needs approval" },
];

const sortFilterOptions = [
  { label: "Newest first", value: "newest" },
  { label: "Oldest first", value: "oldest" },
  { label: "Highest amount first", value: "highest" },
  { label: "Lowest amount first", value: "lowest" },
  { label: "Customer (A-Z)", value: "customer-asc" },
  { label: "Customer (Z-A)", value: "customer-desc" },
];

export const OtcRequestsPage: React.FC = () => {
  const navigate = useNavigate();

  // Filters & State
  const [selectedCurrency, setSelectedCurrency] = useState("NGN");
  const [selectedTimeRange, setSelectedTimeRange] = useState("all");
  const [mainTab, setMainTab] = useState<OtcMainTab>("New");
  const [subTab, setSubTab] = useState<string>("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [currencyFilter, setCurrencyFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [sortBy, setSortBy] = useState("recent");
  const [page, setPage] = useState(1);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const mainTabs = [
    { label: "New", value: "New" },
    { label: "Processing", value: "Processing" },
    { label: "Completed", value: "Completed" },
    { label: "Closed", value: "Closed" },
  ];

  const handleMainTabChange = (value: string) => {
    setMainTab(value as OtcMainTab);
    setSubTab("All");
    setPage(1);
  };

  const subTabs = useMemo(() => {
    if (mainTab === "New") {
      const newItems = INITIAL_OTC_REQUESTS.filter((i) => i.section === "New");
      return [
        { label: "All", count: newItems.length },
        { label: "Under review", count: newItems.filter((i) => i.state === "Under review").length },
        { label: "Request approved", count: newItems.filter((i) => i.state === "Request approved").length },
        { label: "Offer sent", count: newItems.filter((i) => i.state === "Offer sent").length },
        { label: "Offer accepted", count: newItems.filter((i) => i.state === "Offer accepted").length },
        { label: "Needs approval", count: newItems.filter((i) => i.state === "Needs approval").length },
      ];
    }
    const sectionItems = INITIAL_OTC_REQUESTS.filter((i) => i.section === mainTab);
    return [{ label: "All", count: sectionItems.length }];
  }, [mainTab]);

  // Filtering & Sorting
  const filteredRequests = useMemo(() => {
    let list = INITIAL_OTC_REQUESTS.filter(
      (item) => item.section === mainTab || (mainTab === "New" && item.section === "New")
    );

    if (subTab !== "All") {
      list = list.filter((item) => item.state === subTab);
    }

    if (statusFilter !== "All") {
      list = list.filter((item) => item.state === statusFilter);
    }

    if (currencyFilter !== "All") {
      list = list.filter((item) => item.currency === currencyFilter || item.pair.includes(currencyFilter));
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (item) =>
          item.customer.name.toLowerCase().includes(q) ||
          item.transactionId.toLowerCase().includes(q) ||
          item.reference.toLowerCase().includes(q) ||
          item.pair.toLowerCase().includes(q) ||
          item.state.toLowerCase().includes(q)
      );
    }

    switch (sortBy) {
      case "oldest":
        list = [...list].reverse();
        break;
      case "highest":
        list = [...list].sort((a, b) => b.amount - a.amount);
        break;
      case "lowest":
        list = [...list].sort((a, b) => a.amount - b.amount);
        break;
      case "customer-asc":
        list = [...list].sort((a, b) => a.customer.name.localeCompare(b.customer.name));
        break;
      case "customer-desc":
        list = [...list].sort((a, b) => b.customer.name.localeCompare(a.customer.name));
        break;
      case "recent":
      default:
        break;
    }

    return list;
  }, [mainTab, subTab, statusFilter, currencyFilter, searchQuery, sortBy]);

  const itemsPerPage = 8;
  const paginatedRequests = filteredRequests.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const handleCopyId = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    toast.success("Transaction ID copied");
    setTimeout(() => setCopiedId(null), 1500);
  };

  const handleExport = () => {
    const headers = [
      "Date",
      "Amount",
      "Our rate",
      "Requested rate",
      "Currency pair",
      "Customer",
      "Transaction ID",
      "State",
    ];
    const rows = filteredRequests.map((row) => [
      row.date,
      row.amount,
      row.ourRate,
      row.requestedRate,
      row.pair,
      row.customer.name,
      row.transactionId,
      row.state,
    ]);
    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `otc-requests-${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("OTC Requests exported to CSV");
  };

  const formatAmount = (amount: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);

  return (
    <section className="min-h-full bg-[#F3F4F8] md:p-6">
      <div className="rounded-[16px] bg-white p-4 md:p-6">
        {/* Top Header matching KYB & Compliance */}
        <div>
          <HeadingText className="aku-heading-h1-medium text-[#161827]">
            OTC Requests
          </HeadingText>

          {/* Currency & Time Filter Sub-bar */}
          <div className="mt-2.5 flex items-center gap-3 text-[14px]">
            <OtcFilterDropdown
              variant="header"
              value={selectedCurrency}
              options={currencySelectorOptions}
              onChange={setSelectedCurrency}
              icon={
                currencySelectorOptions.find(
                  (opt) => opt.value === selectedCurrency
                )?.icon
              }
            />

            <span className="h-4 w-px bg-[#EEF1FB]" />

            <OtcFilterDropdown
              variant="header"
              labelPrefix="Filter"
              value={selectedTimeRange}
              options={timeFilterOptions}
              onChange={setSelectedTimeRange}
            />
          </div>
        </div>

        {/* Metric Cards */}
        <section
          className="my-6 grid grid-cols-2 gap-3.5 sm:grid-cols-3 xl:grid-cols-5"
          aria-label="OTC Summary Metrics"
        >
        <MetricCard
          label="OTC transaction volume"
          value="₦209,500,000"
          subtext="11.4m requests"
        />
        <MetricCard
          label="Processing"
          value="3"
          valueColorClass="text-[#6F00FF]"
          subtext={
            <>
              <span className="aku-label-medium font-[500] text-[#18A957]">
                +3%
              </span>{" "}
              since last month
            </>
          }
        />
        <MetricCard
          label="Declined"
          value="1,300"
          valueColorClass="text-[#161827]"
          subtext={
            <>
              <span className="aku-label-medium font-[500] text-[#18A957]">
                +3%
              </span>{" "}
              since last month
            </>
          }
        />
        <MetricCard
          label="Rejected"
          value="1,500"
          valueColorClass="text-[#C01515]"
          subtext={
            <>
              <span className="aku-label-medium font-[500] text-[#18A957]">
                +3%
              </span>{" "}
              since last month
            </>
          }
        />
        <MetricCard
          label="Completed"
          value="217"
          valueColorClass="text-[#18A957]"
          subtext={
            <>
              <span className="aku-label-medium font-[500] text-[#18A957]">
                +3%
              </span>{" "}
              since last month
            </>
          }
        />
      </section>

      {/* Main Tabs matching KYB Status Tabs */}
      <Tabs
        items={mainTabs}
        value={mainTab}
        onChange={handleMainTabChange}
      />

      {/* Sub Tabs / Filter Pills */}
      <div className="mt-4 flex items-center gap-4 overflow-x-auto pb-1 text-[12px]">
        {subTabs.map((item) => {
          const isActive = subTab === item.label;
          return (
            <button
              key={item.label}
              type="button"
              onClick={() => {
                setSubTab(item.label);
                setPage(1);
              }}
              className={`flex shrink-0 items-center gap-1.5 transition-all ${
                isActive
                  ? "rounded-[6px] bg-[#F0EBFF] px-3 py-1 font-semibold text-[#6F00FF]"
                  : "px-1 py-1 font-medium text-[#475467] hover:text-[#101828]"
              }`}
            >
              <span>{item.label}</span>
              {item.count > 0 && (
                <span className="flex h-[15px] min-w-[15px] items-center justify-center rounded-full bg-[#12B76A] px-1 text-[9px] font-bold text-white">
                  {item.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Action Bar (Search, Dropdowns, Export) */}
      <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
        <div className="flex flex-wrap items-center gap-2.5">
          {/* Search */}
          <div className="relative flex h-[34px] w-[280px] items-center rounded-[6px] bg-[#F1F4F8] px-3">
            <Search size={14} className="mr-2 text-[#667085]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              placeholder="Search OTC requests"
              className="w-full bg-transparent text-[12px] text-[#101828] outline-none placeholder:text-[#667085]"
            />
          </div>

          {/* Currency Dropdown wrapped in shadcn / Radix popover */}
          <Select
            value={currencyFilter}
            onChange={(val) => {
              setCurrencyFilter(val);
              setPage(1);
            }}
            title="Currency"
            placeholder="All"
            triggerPrefix="Currency"
            options={currencyFilterOptions}
          />

          {/* Status Dropdown wrapped in shadcn / Radix popover */}
          <Select
            value={statusFilter}
            onChange={(val) => {
              setStatusFilter(val);
              setPage(1);
            }}
            title="Status"
            placeholder="All"
            triggerPrefix="Status"
            options={statusFilterOptions}
          />

          {/* Sort Dropdown wrapped in shadcn / Radix popover */}
          <Select
            value={sortBy}
            onChange={(val) => {
              setSortBy(val);
              setPage(1);
            }}
            title="Sort by"
            placeholder="Newest first"
            triggerPrefix="Sort"
            options={sortFilterOptions}
          />
        </div>

        {/* Export Button */}
        <button
          type="button"
          onClick={handleExport}
          className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#6F00FF] hover:text-[#5800CC] transition-colors"
        >
          <FileDown size={17} />
          <span>Export</span>
        </button>
      </div>

      {/* Table (Borderless outer container matching Transaction page & Figma) */}
      <div className="mt-4 overflow-x-auto">
        <Table className="w-full">
          <TableHeader>
            <TableRow className="border-b border-[#F2F4F7] hover:bg-transparent">
              <TableHead className="py-2.5 px-3 text-[12px] font-medium text-[#475467]">
                Date
              </TableHead>
              <TableHead className="py-2.5 px-3 text-[12px] font-medium text-[#475467]">
                Amount
              </TableHead>
              <TableHead className="py-2.5 px-3 text-[12px] font-medium text-[#475467]">
                Our rate
              </TableHead>
              <TableHead className="py-2.5 px-3 text-[12px] font-medium text-[#475467]">
                Requested rate
              </TableHead>
              <TableHead className="py-2.5 px-3 text-[12px] font-medium text-[#475467]">
                Currency pair
              </TableHead>
              <TableHead className="py-2.5 px-3 text-[12px] font-medium text-[#475467]">
                Customer
              </TableHead>
              <TableHead className="py-2.5 px-3 text-[12px] font-medium text-[#475467]">
                Transaction ID
              </TableHead>
              <TableHead className="py-2.5 pr-4 pl-1 text-right w-8 text-[12px] font-medium text-[#475467]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedRequests.map((row) => {
              return (
                <TableRow
                  key={row.id}
                  onClick={() => navigate(`/otc-requests/${row.id}`)}
                  className="group cursor-pointer border-b border-[#F2F4F7] transition-colors hover:bg-[#F9FAFB]"
                >
                  <TableCell className="py-2.5 px-3 text-[13px] font-normal text-[#475467] whitespace-nowrap">
                    {row.date}
                  </TableCell>
                  <TableCell className="py-2.5 px-3 text-[13px] font-semibold text-[#101828] whitespace-nowrap">
                    {formatAmount(row.amount)}
                  </TableCell>
                  <TableCell className="py-2.5 px-3 text-[13px] font-normal text-[#344054] whitespace-nowrap">
                    {row.ourRate}
                  </TableCell>
                  <TableCell className="py-2.5 px-3 text-[13px] font-normal text-[#344054] whitespace-nowrap">
                    {row.requestedRate}
                  </TableCell>
                  <TableCell className="py-2.5 px-3 text-[13px] font-normal text-[#475467] whitespace-nowrap">
                    {row.pair}
                  </TableCell>
                  <TableCell className="py-2.5 px-3 whitespace-nowrap">
                    <div className="flex items-center gap-2 max-w-[155px]">
                      <CustomerAvatar
                        name={row.customer.name}
                        size={20}
                      />
                      <span
                        className="truncate text-[13px] font-normal text-[#344054]"
                        title={row.customer.name}
                      >
                        {row.customer.name}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="py-2.5 px-3 text-[13px] whitespace-nowrap">
                    <div className="flex items-center gap-1.5 text-[13px] text-[#344054]">
                      <span>{row.transactionId}</span>
                      <button
                        type="button"
                        aria-label="Copy transaction ID"
                        onClick={(e) =>
                          handleCopyId(e, row.reference || row.transactionId)
                        }
                        className="rounded p-1 text-[#667085] transition-colors hover:bg-[#EEF1FB] hover:text-[#101828]"
                        title="Copy ID"
                      >
                        {copiedId === (row.reference || row.transactionId) ? (
                          <Check size={14} className="text-[#12B76A]" />
                        ) : (
                          <Copy size={14} />
                        )}
                      </button>
                    </div>
                  </TableCell>
                  {/* Chevron Arrow - visible only on hover */}
                  <TableCell className="py-2.5 pr-4 pl-1 text-right w-8">
                    <ChevronRight
                      size={18}
                      className="text-[#98A2B3] opacity-0 group-hover:opacity-100 transition-opacity ml-auto"
                    />
                  </TableCell>
                </TableRow>
              );
            })}

            {paginatedRequests.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="py-12 text-center text-[13px] text-[#667085]"
                >
                  No OTC requests match the selected filters.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination Footer */}
      <div className="mt-6 border-t border-[#F2F4F7] pt-4">
        <PaginationControls
          currentPage={page}
          totalPages={Math.max(1, Math.ceil(filteredRequests.length / itemsPerPage))}
          onPageChange={setPage}
          className="flex items-center gap-2"
        />
      </div>
      </div>
    </section>
  );
};

export default OtcRequestsPage;
